// Contacts de la gateway (Réglages → Contacts) : fenêtre « Contact », liens mailto, réglages. Un champ vide n'est
// affiché nulle part ; sans aucun contact, rien n'apparaît.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, unwrap } from "../api/client";
import type { components } from "../api/schema";
import { getLang, t } from "../lib/i18n";
import { Button, Field, FormError, Modal, Panel, TextInput, useToast } from "./ui";

export type Contacts = components["schemas"]["ContactsView"];

const ContactsContext = createContext<Contacts | null>(null);

export function ContactsProvider({ value, children }: { value: Contacts | null; children: ReactNode }) {
  return <ContactsContext.Provider value={value}>{children}</ContactsContext.Provider>;
}

/** Contacts à montrer, ou null s'il n'y en a aucun. */
export function useContacts(): Contacts | null {
  const c = useContext(ContactsContext);
  return c && (c.info_email || c.support_email || c.phone || c.hours || c.hours_en) ? c : null;
}

export function MailLink({ address }: { address: string }) {
  return <a className="text-accent hover:underline" href={`mailto:${address}`}>{address}</a>;
}

/** Fenêtre « Contact » : informations, support, téléphone, horaires (champs remplis seulement). */
export function ContactModal({ contacts, onClose }: { contacts: Contacts; onClose: () => void }) {
  const rows: [string, ReactNode][] = [];
  if (contacts.info_email) rows.push([t("Informations"), <MailLink address={contacts.info_email} />]);
  if (contacts.support_email) rows.push([t("Support"), <MailLink address={contacts.support_email} />]);
  if (contacts.phone) {
    rows.push([t("Téléphone"), <a className="text-accent hover:underline" href={`tel:${contacts.phone.replace(/\s/g, "")}`}>
      {contacts.phone}</a>]);
  }
  // horaires dans la langue de l'interface ; un seul rempli : affiché dans les deux langues
  const hours = getLang() === "en" ? contacts.hours_en || contacts.hours : contacts.hours || contacts.hours_en;
  if (hours) rows.push([t("Horaires du support"), hours]);
  return (
    <Modal open title={t("Contact")} onClose={onClose} footer={<Button variant="primary" onClick={onClose}>{t("Fermer")}</Button>}>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-left">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="font-cond text-[13px] font-semibold uppercase tracking-wider text-faint">{label}</dt>
            <dd className="min-w-0 break-words">{value}</dd>
          </div>
        ))}
      </dl>
    </Modal>
  );
}

/** Bouton « Contact » en bas des pages publiques (absent sans contact). */
export function ContactButton() {
  const contacts = useContacts();
  const [open, setOpen] = useState(false);
  if (!contacts) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className="mx-auto text-[13px] text-muted hover:text-ink hover:underline cursor-pointer">{t("Contact")}</button>
      {open && <ContactModal contacts={contacts} onClose={() => setOpen(false)} />}
    </>
  );
}

/** Réglages → Contacts. */
export function ContactsPanel() {
  const toast = useToast();
  const [c, setC] = useState<Contacts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    unwrap(api.GET("/api/contacts/settings")).then(setC).catch((e: Error) => setError(e.message));
  }, []);
  if (!c) return <Panel title={t("Contacts")}><p className="p-4 text-muted">{error ?? t("Chargement…")}</p></Panel>;
  const set = (k: keyof Contacts) => (e: { target: { value: string } }) => setC({ ...c, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      setC(await unwrap(api.PUT("/api/contacts/settings", { body: c })));
      toast(t("Contacts enregistrés"));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title={t("Contacts")}>
      <div className="flex flex-col gap-4 p-4">
        <p className="text-[13px] text-muted">
          {t("Affichés sur les pages de connexion et de demande d'accès (bouton « Contact »), et dans le menu du compte. Les e-mails des inscriptions répondent à l'adresse du support et la rappellent en bas du message. Un champ vide n'est affiché nulle part.")}
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("Adresse pour les informations")} htmlFor="ct-info">
            <TextInput id="ct-info" type="email" maxLength={254} value={c.info_email} onChange={set("info_email")}
              placeholder={t("info@exemple.com")} />
          </Field>
          <Field label={t("Adresse du support")} htmlFor="ct-support">
            <TextInput id="ct-support" type="email" maxLength={254} value={c.support_email} onChange={set("support_email")}
              placeholder={t("support@exemple.com")} />
          </Field>
          <Field label={t("Téléphone (facultatif)")} htmlFor="ct-phone">
            <TextInput id="ct-phone" type="tel" maxLength={30} value={c.phone} onChange={set("phone")} />
          </Field>
          <Field label={t("Horaires du support, en français (facultatif)")} htmlFor="ct-hours">
            <TextInput id="ct-hours" maxLength={80} value={c.hours} onChange={set("hours")} placeholder="lun–ven 9 h–18 h" />
          </Field>
          <Field label={t("Horaires du support, en anglais (facultatif)")} htmlFor="ct-hours-en"
            hint={t("Chacun s'affiche dans sa langue ; un seul rempli s'affiche dans les deux.")}>
            <TextInput id="ct-hours-en" maxLength={80} value={c.hours_en} onChange={set("hours_en")} placeholder="Mon–Fri 9am–6pm" />
          </Field>
        </div>
        <FormError message={error} />
        <div><Button variant="primary" onClick={save} disabled={busy}>{t("Enregistrer")}</Button></div>
      </div>
    </Panel>
  );
}
